import { cn } from "@/lib/utils";
import { useCallback, useState } from "react";

/**
 * Brand-green range control. Fill tracks thumb continuously.
 * Controlled value should be a continuous percent (avoid Math.round on value).
 */
export function RangeSlider({
	value,
	onChange,
	min = 0,
	max = 100,
	step = 0.1,
	disabled,
	label,
	valueLabel,
	className,
	id = "range-slider",
}: {
	value: number;
	onChange: (v: number) => void;
	min?: number;
	max?: number;
	step?: number;
	disabled?: boolean;
	label?: string;
	valueLabel?: string;
	className?: string;
	id?: string;
}) {
	const [dragging, setDragging] = useState(false);
	const rangeOk = Number.isFinite(max) && Number.isFinite(min) && max > min;
	const safeMax = rangeOk ? max : min + 1;
	const raw = Number.isFinite(value) ? value : min;
	const clamped = Math.min(safeMax, Math.max(min, raw));
	const pct = rangeOk ? ((clamped - min) / (safeMax - min)) * 100 : 0;
	const isDisabled = Boolean(disabled || !rangeOk);

	const commit = useCallback(
		(next: number) => {
			if (isDisabled) return;
			const v = Math.min(safeMax, Math.max(min, next));
			onChange(v);
		},
		[isDisabled, min, safeMax, onChange],
	);

	return (
		<div className={cn("range-slider space-y-2", className)}>
			{(label || valueLabel) && (
				<div className="flex items-center justify-between text-xs">
					{label ? (
						<label htmlFor={id} className="font-semibold text-muted-foreground">
							{label}
						</label>
					) : (
						<span />
					)}
					{valueLabel ? (
						<span className="font-mono-amount font-semibold text-primary">
							{valueLabel}
						</span>
					) : null}
				</div>
			)}
			<div className="relative h-11 w-full touch-none select-none">
				<div
					className="pointer-events-none absolute left-0 right-0 top-1/2 h-2.5 -translate-y-1/2 rounded-full bg-muted ring-1 ring-border/60"
					aria-hidden
				/>
				<div
					className={cn(
						"pointer-events-none absolute left-0 top-1/2 z-[1] h-2.5 -translate-y-1/2 rounded-full bg-primary shadow-[0_0_10px_rgba(0,200,83,0.4)]",
						!dragging && "transition-[width] duration-75 ease-out",
					)}
					style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
					aria-hidden
				/>
				<input
					id={id}
					type="range"
					min={min}
					max={safeMax}
					step={step}
					value={clamped}
					disabled={isDisabled}
					onChange={(e) => commit(Number(e.target.value))}
					onInput={(e) =>
						commit(Number((e.target as HTMLInputElement).value))
					}
					onPointerDown={() => setDragging(true)}
					onPointerUp={() => setDragging(false)}
					onPointerCancel={() => setDragging(false)}
					onBlur={() => setDragging(false)}
					className="range-slider__input absolute inset-0 z-10 h-full w-full cursor-pointer appearance-none bg-transparent disabled:cursor-not-allowed disabled:opacity-50"
					aria-valuemin={min}
					aria-valuemax={safeMax}
					aria-valuenow={clamped}
					aria-label={label ?? "Amount percent"}
				/>
			</div>
			<div className="flex justify-between gap-2">
				{(
					[
						["0%", min],
						["50%", min + (safeMax - min) * 0.5],
						["Max", safeMax],
					] as const
				).map(([lab, v]) => (
					<button
						key={lab}
						type="button"
						disabled={isDisabled}
						className="min-h-8 rounded-lg px-2 text-[11px] font-semibold text-primary hover:bg-primary/10 disabled:opacity-40"
						onClick={() => commit(Number(v))}
					>
						{lab}
					</button>
				))}
			</div>
		</div>
	);
}
